import { FormGroup, MenuItem, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IMultiSelectProps, MultiSelect, Select } from "@blueprintjs/select";
import { get as _get } from "lodash";
import React from "react";

import Query from "core/api/QueryComponent";
import Class from "core/util/Class";
import { includesIgnoreCase } from "../../../../core/util/StringUtil";
import { registerField } from "../../register";
import { IAbstractEntity, ID, IEntitySelectOptions, IEntitySelectProps, IEntitySelectState } from "./IEntitySelect";

interface IInternalProps<E, P, V> extends IEntitySelectProps<E, P, V> {
	entity: Class<E>;

}

export class EntityMultiSelect<E, P> extends React.Component<IInternalProps<E, P, any[]>, IEntitySelectState> {

	public static ofType<E, P = {}>(entity: Class<any>, params: P, options?: IEntitySelectOptions<E>) {

		return (props: IEntitySelectProps<E, P, any[]>) => <EntityMultiSelect entity={entity} params={params} options={options} {...props} />;
	}

	public static registered<E, P = {}>(entity: Class<any>, params: P, options?: IEntitySelectOptions<E>) {
		return registerField(EntityMultiSelect.ofType(entity, params, options), "array");
	}

	public state: IEntitySelectState = { query: "" };

	public render() {
		const {
			entity,
			formData,
			options = {} as any,
			params,
			schema = {}
		} = this.props;

		const nameKey = this.getNameKey();

		const p = { ...params || {} } as P;

		if (options.lazyLoad) {
			p['query'] = this.state.query;
		}

		return (
			<FormGroup label={schema.title} helperText={schema.description}>
				<Query.Many entity={entity} args={p}>
					{
						({ data, loading }) => (
							<MultiSelect
								items={data || []}
								itemPredicate={(query: string, item: E) => includesIgnoreCase(_get(item, nameKey), query)}
								itemRenderer={(item, itemProps) => this.renderItem(item, itemProps, this.isSelected(item))}
								onItemSelect={(item: E) => this.onItemSelect(item)}
								onQueryChange={(query: string) => this.setState({query})}
								resetOnSelect={true}
								selectedItems={this.getSelectedItems(data, formData)}
								tagInputProps={
									{
										disabled: loading,
										fill: true,
										leftIcon: loading && <Spinner />,
										onRemove: (val, idx) => this.handleRemove(val, idx),
										placeholder: schema.title
									}
								}
								tagRenderer={(item: E) => _get(item, nameKey)}
							/>
						)
					}
				</Query.Many>
			</FormGroup>
		);
	}

	private renderItem = (item: E, { modifiers, handleClick }, isSelected: boolean): JSX.Element => {
		if (!modifiers.matchesPredicate) {
			return null;
		}

		const { options = {} } = this.props;

		// TODO add ability to provide custom details renderer for active items
		return (
			<MenuItem
				active={modifiers.active}
				icon={isSelected ? IconNames.TICK : undefined}
				key={_get(item, this.getValueKey())}
				labelElement={options.getSecondaryLabel && options.getSecondaryLabel(item)}
				onClick={handleClick}
				text={_get(item, this.getNameKey())}
				shouldDismissPopover={false}
			/>
		);
	}

	private onItemSelect = (item: E): void => {
		const { onChange, formData } = this.props;

		if (!Array.isArray(formData)) {
			onChange([_get(item, this.getValueKey())]);
			return;
		}

		const nextData = formData.indexOf(_get(item, this.getValueKey())) > -1 ? formData.filter((id) => _get(item, this.getValueKey()) !== id) : [...formData.concat([_get(item, this.getValueKey())])];
		onChange(nextData);
	}

	private handleRemove = (_, index): void => {
		const nextVal: any[] = this.props.formData.filter((__, i) => i !== index);
		this.props.onChange(nextVal.length > 0 ? nextVal : undefined);
	}

	private isSelected = (item: E): boolean => (this.props.formData || []).indexOf(_get(item, this.getValueKey())) > -1;

	private getSelectedItems = (items: E[], activeIds: ID[]): E[] => (items || []).filter((e) => (activeIds || []).indexOf(_get(e, this.getValueKey())) > -1);

	private getNameKey = () => this.props.options && this.props.options.nameKey || 'name';
	private getValueKey = () => this.props.options && this.props.options.valueKey || 'value';
}



// WEBPACK FOOTER //
// ./src/common/form/fields/entitySelect/EntityMultiSelect.tsx